'use client';

import { Alert, AlertClose, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { SignIn, useUser } from '@clerk/clerk-react';
import { ApiError, AuthenticationError, NetworkError } from '../hooks/useAuthenticatedApi';
import {
  BookOpen,
  Clock,
  MessageSquare,
  PartyPopper,
  RefreshCw,
  Star,
  Zap,
  Book,
  BotMessageSquare,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useSubscription } from '../hooks/useSubscription';

import type { CreditResponse } from '@/types/api';
import { subscriptionOptions } from '@/config/subscriptions';
import { SubscriptionStatus } from '../components/SubscriptionStatus';

interface LoginResponse extends CreditResponse {
  hasSubscription: boolean;
  subscription: string;
  defaultModel: string;
  useDescriptiveChat: boolean;
  ageRange: number;
  signUpStatus?: 'onboarded' | 'waiting';
}

const Home = () => {
  const { user } = useUser();
  const { apiCall } = useAuthenticatedApi();
  const { setSubscriptionSpend, setSubscriptionLimit, subscriptionSpend, subscriptionLimit } = useSubscription();
  const { toast } = useToast();
  const location = useLocation();

  const [signUpStatus, setSignUpStatus] = useState<'onboarded' | 'waiting' | undefined>();
  const [onboardedDismissed, setOnboardedDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);
  const [showPanel, setShowPanel] = useState(false);
  const [panelMessage, setPanelMessage] = useState('');
  const [versionMismatch, setVersionMismatch] = useState(false);
  const [buildInfo, setBuildInfo] = useState<{
    gitSha: string;
    date: string;
    build: string;
  } | null>(null);
  const [currentlySubscribed, setCurrentlySubscribed] = useState(false);

  const initializeUser = useCallback(
    async (forceRefresh = false) => {
      // Skip if already loading or if not enough time has passed (5 minutes)
      const now = Date.now();
      if (isLoading || (!forceRefresh && lastFetchTime && now - lastFetchTime < 5 * 60 * 1000)) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await apiCall<LoginResponse>('/get-user', 'POST', {
          action: 'login',
        });
        if (response === undefined) return;

        // Validate response data before using it
        if (response && typeof response === 'object') {
          // Handle region if it's provided in the response
          if ('region' in response && typeof response.region === 'string') {
            localStorage.setItem('region', response.region);
          }

          // Set subscription values if they exist
          if ('creditsConsumed' in response && typeof response.creditsConsumed === 'number') {
            setSubscriptionSpend(response.creditsConsumed);
          }

          if ('creditLimit' in response && typeof response.creditLimit === 'number') {
            setSubscriptionLimit(response.creditLimit);
          }
          const subscriptionType = response.subscription;

          // Handle sign up status
          if (response.signUpStatus === 'onboarded' || response.signUpStatus === 'waiting') {
            setSignUpStatus(response.signUpStatus);

            if (response.signUpStatus === 'onboarded') {
              localStorage.setItem('onboarded', 'true');
            }
          }

          // Handle URL params
          const params = new URLSearchParams(location.search);
          const action = params.get('action');
          if (action === 'payment' || action === 'subscription') {
            setShowPanel(true);
            if (action === 'payment') {
              setPanelMessage(`Thanks for your purchase! You now have a total of ${response.creditLimit} credits.`);
            } else if (action === 'subscription') {
              // find in subscriptionOptions
              let period = 'month';
              let newSubscription = subscriptionOptions['monthly'].find((sub) => sub.subscription === subscriptionType);
              if (!newSubscription) {
                period = 'quarter';
                newSubscription = subscriptionOptions['quarterly'].find((sub) => sub.subscription === subscriptionType);
              }
              setPanelMessage(
                `Thanks for your purchase! You will receive ${newSubscription?.credits} credits per ${period} on the ${newSubscription?.price} plan. Credits rollover for three months.`
              );
            }
          }
          setCurrentlySubscribed(!['', 'cancelled', 'ending', 'trial'].includes(response.subscription));
        }

        // Check version from build.json against window.release_version
        try {
          const buildInfoResponse = await fetch('/build.json');
          if (buildInfoResponse.ok) {
            const buildInfoData = await buildInfoResponse.json();
            setBuildInfo(buildInfoData);

            if (buildInfoData.v && buildInfoData.v !== 'dev' && window.release_version !== buildInfoData.v) {
              console.warn(
                `Version mismatch: running ${window.release_version} but build.json has ${buildInfoData.v}`,
                `Build date: ${new Date(buildInfoData.date).toLocaleString()}`
              );
              setVersionMismatch(true);
            } else {
              setVersionMismatch(false);
            }
          }
        } catch (error) {
          console.warn('Error fetching build.json:', error);
        }

        // Update last fetch time
        setLastFetchTime(now);
      } catch (error: unknown) {
        console.error('Error initializing user:', error);

        let errorMessage = 'Failed to initialize user data';

        if (error instanceof AuthenticationError) {
          errorMessage = error.message;
        } else if (error instanceof NetworkError) {
          errorMessage = error.message;
        } else if (error instanceof ApiError) {
          errorMessage = error.message;
        }

        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [apiCall, setSubscriptionSpend, setSubscriptionLimit, toast, isLoading, lastFetchTime, location.search]
  );

  useEffect(() => {
    if (user) {
      initializeUser();
      const dismissed = localStorage.getItem('onboardedDismissed') === 'true';
      setOnboardedDismissed(dismissed);
    }
  }, [user, initializeUser]);

  const handleDismiss = () => {
    localStorage.setItem('onboardedDismissed', 'true');
    setOnboardedDismissed(true);
    setSignUpStatus(undefined);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const dismissVersionMismatch = () => {
    setVersionMismatch(false);
  };

  if (!user) {
    return <SignIn />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Welcome to Creative AI Writer!</h1>
        <Button variant="outline" size="sm" onClick={() => initializeUser(true)} disabled={isLoading} className="gap-2">
          {isLoading ? 'Refreshing...' : 'Refresh'}
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      <p className="mb-4">{('Hello ' + user.firstName).trim()}!</p>

      {signUpStatus === 'waiting' && (
        <Alert className="mb-6 bg-muted/50">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <AlertTitle>Welcome to Creative AI Writer!</AlertTitle>
          <AlertDescription>
            <p>We're thrilled to see so much interest in our platform.</p>
            <p>
              Due to high demand, we're staggering our onboarding to ensure the best experience for everyone. You're on
              our waiting list, and your spot is reserved!
            </p>
            <p>We'll notify you by email as soon as the next batch opens. Keep an eye on your inbox!</p>
          </AlertDescription>
          <AlertClose onClick={handleDismiss} />
        </Alert>
      )}

      {signUpStatus === 'onboarded' && !onboardedDismissed && (
        <Alert className="mb-6 bg-muted/50">
          <PartyPopper className="h-5 w-5 text-muted-foreground" />
          <AlertTitle>Welcome aboard!</AlertTitle>
          <AlertDescription>
            <p>We're thrilled to have you as one of our early adopters!</p>
            <p>
              Take a look around and explore all the tools at your fingertips. If you have any questions or feedback,
              feel free to reach out.
            </p>
          </AlertDescription>
          <AlertClose onClick={handleDismiss} />
        </Alert>
      )}

      {showPanel && (
        <Alert className="mb-6 bg-muted/50">
          <AlertTitle>Thank you!</AlertTitle>
          <AlertDescription>{panelMessage}</AlertDescription>
          <AlertClose onClick={() => setShowPanel(false)} />
        </Alert>
      )}

      {versionMismatch && buildInfo && (
        <Alert className="mb-6 bg-yellow-50 border-yellow-200">
          <RefreshCw className="h-5 w-5 text-yellow-600" />
          <AlertTitle>New version available</AlertTitle>
          <AlertDescription>
            <p>A new version of the app is available (Build {buildInfo.build}).</p>
            <p>Would you like to reload to get the latest features and fixes?</p>
            <div className="flex gap-2 mt-2">
              <Button variant="default" size="sm" onClick={handleReload} className="bg-yellow-600 hover:bg-yellow-700">
                Yes, reload now
              </Button>
              <Button variant="outline" size="sm" onClick={dismissVersionMismatch}>
                Later
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 mt-8">
        {/* First row - action cards from Dashboard */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
              <CardDescription>Create and manage your stories, speeches, or books</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/documents">
                <Button className="w-full">
                  <Book className="mr-2 h-4 w-4" /> View Documents
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>AI Toolbox</CardTitle>
              <CardDescription>Lots of goodies for developing ideas</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/ai-toolbox">
                <Button className="w-full">
                  <BotMessageSquare className="mr-2 h-4 w-4" /> Try the AI Toolbox
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Character Chat</CardTitle>
              <CardDescription>Chat with characters from your stories</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/documents">
                <Button className="w-full">
                  <MessageSquare className="mr-2 h-4 w-4" /> Start Chatting
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Information cards from Home */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BookOpen className="mr-2" />
              Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              To give you an idea, you can make{' '}
              <Link to="/documents" className="text-primary hover:underline">
                documents
              </Link>{' '}
              (a story, a speech or even a book) - then you can review it, get a quick rating of the story or even chat
              with the characters you have in the story.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="mr-2" />
              Document chat
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              After you do a <strong>document review</strong> now you can chat with the AI about the review. Ask for
              clarifications or more exploration of the story. Go nuts!
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Star className="mr-2" />
              Character chat!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              Chat with the character of your documents?! Yes! You can write a story for example and then chat with the
              characters in the story. Try it, it's super fun. If you are stuck on what to do in the story why not just
              ask them what they want to do!
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Zap className="mr-2" />
              Credits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              The site runs on "credits" - you can see your current credit use and subscription status in the header
              above or on the{' '}
              <Link to="/settings" className="text-primary hover:underline">
                settings
              </Link>{' '}
              page. The AI calls are pricey so small AI jobs like expanding a bit of text use maybe less than a credit
              but larger jobs like reviewing a document will take a few. If you review a book that has for example
              50,000 words (a real decent book length), that will churn through a lot of credits.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <SubscriptionStatus
          subscriptionSpend={subscriptionSpend}
          subscriptionLimit={subscriptionLimit}
          currentlySubscribed={currentlySubscribed}
        />
      </div>
    </div>
  );
};

export default Home;
